// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useMemo } from 'react';
import { stringify } from 'query-string';
import { uniqueId, omit } from 'lodash';
import { useTranslation } from 'react-i18next';

import SerialNumbersComponent from 'Components/Service/SerialNumbers';
import { API_SERIAL_NUMBERS } from 'Constants';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useAsyncError from 'Hooks/useAsyncError';
import useNumberUtils from 'Hooks/useNumberUtils';
import useAnalytics from 'Hooks/useAnalytics';

const defaultSortId = 'serial_number_date-desc';
const sortOptionIds = [
  { id: 'serial_number-asc', label: 'pages.serialNumber', direction: 'orders.ascending' },
  { id: 'serial_number-desc', label: 'pages.serialNumber', direction: 'orders.descending' },
  { id: 'product_code-asc', label: 'pages.productCode', direction: 'orders.ascending' },
  { id: 'product_code-desc', label: 'pages.productCode', direction: 'orders.descending' },
  { id: 'manufacturer_name-asc', label: 'pages.manufacturerName', direction: 'orders.ascending' },
  { id: 'manufacturer_name-desc', label: 'pages.manufacturerName', direction: 'orders.descending' },
  { id: 'invoice_number-asc', label: 'pages.invoiceNumber', direction: 'orders.ascending' },
  { id: 'invoice_number-desc', label: 'pages.invoiceNumber', direction: 'orders.descending' },
  { id: 'serial_number_date-asc', label: 'pages.serialNumber', direction: 'orders.ascending' },
  { id: 'serial_number_date-desc', label: 'pages.serialNumber', direction: 'orders.descending' },
  { id: 'serial_number_warranty-asc', label: 'pages.serialNumberWarranty', direction: 'orders.ascending' },
  { id: 'serial_number_warranty-desc', label: 'pages.serialNumberWarranty', direction: 'orders.descending' },
];
const allowedSortIds = sortOptionIds.map(option => option.id);

const getSanitizedQuery = query => {
  const sanitizedQuery = { ...(query || {}) };
  const page = Number(sanitizedQuery.page);
  const sortId = `${ sanitizedQuery.order_by }-${ sanitizedQuery.order_direction }`;

  if ('page' in sanitizedQuery && (!Number.isInteger(page) || page < 1)) {
    delete sanitizedQuery.page;
  }

  if (
    ('order_by' in sanitizedQuery || 'order_direction' in sanitizedQuery)
    && !allowedSortIds.includes(sortId)
  ) {
    delete sanitizedQuery.order_by;
    delete sanitizedQuery.order_direction;
  }

  return sanitizedQuery;
};

const SerialNumbers = memo(() => {
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetcher = useFetch();
  const { location, query, push } = useRouter();
  const throwError = useAsyncError();
  const { formatDate } = useNumberUtils();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const sortOptions = useMemo(() => sortOptionIds.map(option => ({
    id: option.id,
    label: `${ t(option.label) } ${ t(option.direction) }`
  })), [t]);

  const defaultSort = sortOptions[9]; // serial_number_date-desc
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item?.id === currentSortId) || defaultSort
      : defaultSort
  );

  useEffect(() => {
    // check if query has sort
    if (Object.keys(omit(query || {}, ['serial_number', 'product_code', 'date_start', 'date_end', 'brand[]', 'page'])).length) {
      const { order_by: sortBy, order_direction: sortDirection } = query;
      setSort(sortOptions.find(item => item?.id === `${ sortBy }-${ sortDirection }`) || defaultSort);
    }
  }, []);

  const onMobileOrderChange = sort => {
    const [sortBy, sortDirection] = sort.split('-');
    analytics.track('sort_serial_numbers', { order_by: sortBy, order_direction: sortDirection });

    if (sortDirection === 'none' || sort === defaultSortId) {
      setSort(sortOptions.find(item => item?.id === defaultSortId));
      push({
        location: location.pathname,
        search: stringify(omit(query || {}, ['order_by', 'order_direction']))
      });
      return;
    }

    setSort(sortOptions.find(item => item?.id === sort));

    const queryParams = {
      ...(omit(query || {}, ['order_by', 'order_direction', 'page'])),
      order_by: sortBy,
      order_direction: sortDirection
    };

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const getSerialNumbers = async () => {
    setLoading(true);
    let path = API_SERIAL_NUMBERS;
    const sanitizedQuery = getSanitizedQuery(query);

    if (Object.keys(sanitizedQuery).length) {
      path += `?${ stringify(sanitizedQuery) }`;
    }

    fetcher(path).then(res => {
      const resPagination = {
        currentPage: res?.currentPage,
        recordsPerPage: res?.recordsPerPage,
        totalPages: res?.totalPages,
        totalRecords: res?.totalRecords
      };

      const mappedSerialNumbers = (res?.items || []).map(item => ({
        ...item,
        id: uniqueId(`${ item.serialNumber }-`),
        serialNumberDate: formatDate(item.serialNumberDate, { timeStyle: undefined }),
        serialNumberWarranty: formatDate(item.serialNumberWarranty, { timeStyle: undefined })
      }));
      const mappedManufacturers = (res?.manufacturers || []).map(manufacturer => ({
        ...manufacturer,
        id: manufacturer.id,
        value: manufacturer.id,
        label: manufacturer.name,
      }));
      setSerialNumbers(mappedSerialNumbers);
      setManufacturers(mappedManufacturers);
      setPagination(resPagination);
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Get Serial Numbers Requests on page load
  useEffect(() => {
    getSerialNumbers();
  }, [location.pathname, query]);

  return (
    <SerialNumbersComponent
      serialNumbers={ serialNumbers || [] }
      manufacturers={ manufacturers || [] }
      sortOptions={ sortOptions }
      currentSort={ currentSort }
      defaultSortId={ defaultSortId }
      handleMobileOrderChange={ onMobileOrderChange }
      pagination={ pagination }
      loading={ loading }
    />
  );
});

SerialNumbers.displayName = 'SerialNumbers';

export default SerialNumbers;
